import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';

const getAuthHeader = () => {
  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  return { headers: { Authorization: `Bearer ${token}` } };
};

const API = process.env.REACT_APP_API_BASE_URL || '';
const IMG_BASE = process.env.REACT_APP_API_BASE_URL
  ? process.env.REACT_APP_API_BASE_URL.replace('/api', '')
  : '';

// ─── HELPERS ─────────────────────────────────────────────────────
const parsePhotos = (raw) => {
  if (!raw) return [];
  try {
    const arr = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
};

const photoUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  if (path.includes('/uploads/')) {
    const idx = path.indexOf('/uploads/');
    return `${IMG_BASE}${path.substring(idx)}`;
  }
  return `${IMG_BASE}${path}`;
};

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-PK', { day: 'numeric', month: 'short', year: 'numeric' });
};
const fmtTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit' });
};
const fmtDateTime = (d) => {
  if (!d) return null;
  const dt = new Date(d);
  return dt.toLocaleDateString('en-PK', { day: 'numeric', month: 'short' }) + ' · ' +
    dt.toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit' });
};

const statusMeta = (s) => ({
  completed:     { label: 'Completed',   color: '#22C55E', bg: '#22C55E18' },
  scheduled:     { label: 'Scheduled',   color: '#38BDF8', bg: '#38BDF818' },
  started:       { label: 'In Progress', color: '#F59E0B', bg: '#F59E0B18' },
  before_photos: { label: 'In Progress', color: '#F59E0B', bg: '#F59E0B18' },
  after_photos:  { label: 'In Progress', color: '#F59E0B', bg: '#F59E0B18' },
  in_progress:   { label: 'In Progress', color: '#F59E0B', bg: '#F59E0B18' },
  rescheduled:   { label: 'Rescheduled', color: '#F87171', bg: '#F8717118' },
}[s] || { label: s || 'Unknown', color: '#94A3B8', bg: 'transparent' });

// ─── AVG SERVICE TIME ─────────────────────────────────────────────
const calcAvgServiceTime = (jobs) => {
  const valid = jobs.filter(j =>
    j.status === 'completed' && j.started_at && j.completed_at &&
    (() => {
      const diff = (new Date(j.completed_at) - new Date(j.started_at)) / 60000;
      return diff > 0 && diff < 1440;
    })()
  );
  if (!valid.length) return null;
  const totalMins = valid.reduce((acc, j) =>
    acc + (new Date(j.completed_at) - new Date(j.started_at)) / 60000, 0);
  const avg = totalMins / valid.length;
  const h = Math.floor(avg / 60);
  const m = Math.round(avg % 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

const calcPanelsThisMonth = (jobs) => {
  const now = new Date();
  return jobs
    .filter(j => {
      const d = new Date(j.scheduled_date);
      return (
        j.status === 'completed' &&
        d.getFullYear() === now.getFullYear() &&
        d.getMonth() === now.getMonth()
      );
    })
    .reduce((acc, j) => {
      const v = parseInt(j.total_panels_cleaned ?? j.panel_count ?? j.total_panels ?? j.panels_cleaned ?? 0);
      return acc + (isNaN(v) ? 0 : v);
    }, 0);
};

const buildDailyData = (jobs, monthKey) => {
  if (!monthKey) return [];
  const [yr, mo] = monthKey.split('-').map(Number);
  const daysInMonth = new Date(yr, mo, 0).getDate();
  const map = {};
  for (let d = 1; d <= daysInMonth; d++) {
    map[d] = { day: d, total: 0, completed: 0, scheduled: 0 };
  }
  jobs.forEach(j => {
    const d = new Date(j.scheduled_date);
    if (d.getFullYear() === yr && d.getMonth() + 1 === mo) {
      const day = d.getDate();
      if (map[day]) {
        map[day].total++;
        if (j.status === 'completed') map[day].completed++;
        if (j.status === 'scheduled')  map[day].scheduled++;
      }
    }
  });
  return Object.values(map);
};

// ─── THEME ICON ───────────────────────────────────────────────────
const ThemeIcon = ({ isDark }) => isDark ? (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="5"/>
    <line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
    <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
  </svg>
) : (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
  </svg>
);

// ─── LIGHTBOX ─────────────────────────────────────────────────────
const Lightbox = ({ images, startIndex, onClose }) => {
  const [current, setCurrent] = useState(startIndex || 0);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setCurrent(c => (c + 1) % images.length);
      if (e.key === 'ArrowLeft')  setCurrent(c => (c - 1 + images.length) % images.length);
    };
    window.addEventListener('keydown', handleKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [images.length, onClose]);

  if (!images || images.length === 0) return null;

  const modal = (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
        zIndex: 99999,
        background: 'rgba(0,0,0,0.93)',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        boxSizing: 'border-box',
        padding: 20,
      }}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        style={{
          position: 'fixed', top: 18, right: 18,
          background: 'var(--nd-w12)', border: '1px solid var(--nd-tx25)',
          borderRadius: '50%', width: 44, height: 44, cursor: 'pointer',
          color: 'var(--nd-text-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M18 6L6 18M6 6l12 12"/>
        </svg>
      </button>

      {/* Prev arrow */}
      {images.length > 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); setCurrent(c => (c - 1 + images.length) % images.length); }}
          style={{
            position: 'fixed', left: 16, top: '50%', transform: 'translateY(-50%)',
            background: 'var(--nd-w12)', border: '1px solid var(--nd-tx25)',
            borderRadius: '50%', width: 50, height: 50, cursor: 'pointer',
            color: 'var(--nd-text-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M15 18l-6-6 6-6"/></svg>
        </button>
      )}

      {/* Content — click doesn't bubble to backdrop */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14,
          maxWidth: '90vw', maxHeight: '90vh',
        }}
      >
        <img
          key={current}
          src={photoUrl(images[current])}
          alt={`Photo ${current + 1}`}
          style={{
            maxWidth: '82vw',
            maxHeight: '70vh',
            width: 'auto',
            height: 'auto',
            objectFit: 'contain',
            borderRadius: 10,
            boxShadow: '0 8px 60px rgba(0,0,0,0.7)',
            display: 'block',
          }}
        />

        {images.length > 1 && (
          <div style={{ color: 'var(--nd-tx45)', fontSize: 13, fontWeight: 600, letterSpacing: '0.1em' }}>
            {current + 1} / {images.length}
          </div>
        )}

        {images.length > 1 && (
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', justifyContent: 'center', maxWidth: '80vw' }}>
            {images.map((img, i) => (
              <div
                key={i}
                onClick={() => setCurrent(i)}
                style={{
                  width: 48, height: 48, borderRadius: 6, overflow: 'hidden', cursor: 'pointer',
                  border: i === current ? '2px solid #38BDF8' : '2px solid var(--nd-w20)',
                  opacity: i === current ? 1 : 0.5, transition: 'all 0.15s', flexShrink: 0,
                }}
              >
                <img src={photoUrl(img)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}/>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Next arrow */}
      {images.length > 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); setCurrent(c => (c + 1) % images.length); }}
          style={{
            position: 'fixed', right: 16, top: '50%', transform: 'translateY(-50%)',
            background: 'var(--nd-w12)', border: '1px solid var(--nd-tx25)',
            borderRadius: '50%', width: 50, height: 50, cursor: 'pointer',
            color: 'var(--nd-text-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M9 18l6-6-6-6"/></svg>
        </button>
      )}
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

// ─── CLICKABLE IMAGE GRID ─────────────────────────────────────────
const ClickablePhotoGrid = ({ photos, label, labelColor, borderColor, isDark, T }) => {
  const [lightbox, setLightbox] = useState(null);

  if (!photos || photos.length === 0) return null;

  return (
    <div style={{ marginTop: 18 }}>
      <div style={{
        fontSize: 10, fontWeight: 700, letterSpacing: '0.1em',
        color: labelColor || T.muted, marginBottom: 8, marginTop: 0, paddingBottom: 6,
        borderBottom: `1px solid ${T.border}`,
      }}>
        {label} ({photos.length})
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 6 }}>
        {photos.map((p, i) => (
          <div
            key={i}
            onClick={() => setLightbox(i)}
            style={{
              position: 'relative', cursor: 'pointer', borderRadius: 7, overflow: 'hidden',
              border: `1px solid ${borderColor || T.border}`,
              transition: 'transform 0.15s, box-shadow 0.15s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'scale(1.03)';
              e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.4)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <img
              src={photoUrl(p)}
              alt={`${label} ${i + 1}`}
              style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block' }}
            />
            {/* Hover overlay */}
            <div style={{
              position: 'absolute', inset: 0, background: 'rgba(0,0,0,0)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background 0.15s',
            }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.35)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0)'}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--nd-tx85)" strokeWidth="2" style={{ opacity: 0, transition: 'opacity 0.15s' }}
                ref={el => {
                  if (el) {
                    el.parentElement.addEventListener('mouseenter', () => el.style.opacity = '1');
                    el.parentElement.addEventListener('mouseleave', () => el.style.opacity = '0');
                  }
                }}
              >
                <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
              </svg>
            </div>
          </div>
        ))}
      </div>
      {lightbox !== null && (
        <Lightbox images={photos} startIndex={lightbox} onClose={() => setLightbox(null)}/>
      )}
    </div>
  );
};

// ─── BEFORE/AFTER SLIDER ─────────────────────────────────────────
const BeforeAfterSlider = ({ before, after, height = 220, isDark, beforeTs, afterTs, beforePhotos, afterPhotos }) => {
  const [pos, setPos] = useState(50);
  const [lightbox, setLightbox] = useState(null);
  const dragging = useRef(false);
  const ref = useRef(null);

  const move = useCallback((clientX) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    setPos(Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100)));
  }, []);

  useEffect(() => {
    const up = () => { dragging.current = false; };
    const mv = (e) => { if (dragging.current) move(e.clientX || e.touches?.[0]?.clientX); };
    window.addEventListener('mouseup', up);
    window.addEventListener('mousemove', mv);
    window.addEventListener('touchend', up);
    window.addEventListener('touchmove', mv, { passive: true });
    return () => {
      window.removeEventListener('mouseup', up);
      window.removeEventListener('mousemove', mv);
      window.removeEventListener('touchend', up);
      window.removeEventListener('touchmove', mv);
    };
  }, [move]);

  const emptyBg = isDark ? 'var(--nd-surface-2)' : 'var(--nd-text)';

  // All before + after images combined for lightbox
  const allBeforePhotos = beforePhotos || (before ? [before] : []);
  const allAfterPhotos  = afterPhotos  || (after  ? [after]  : []);

  return (
    <div>
      <div ref={ref}
        style={{ position: 'relative', height, borderRadius: 12, overflow: 'hidden', cursor: 'col-resize', userSelect: 'none', background: emptyBg }}
        onMouseDown={(e) => { dragging.current = true; move(e.clientX); }}
        onTouchStart={(e) => { dragging.current = true; move(e.touches[0].clientX); }}
      >
        {!before && !after ? (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748B', fontSize: 14 }}>No photos available</div>
        ) : (
          <>
            {after && <img src={photoUrl(after)} alt="after" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />}
            <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', width: `${pos}%` }}>
              {before && <img src={photoUrl(before)} alt="before" style={{ position: 'absolute', inset: 0, width: ref.current?.offsetWidth || '100%', height: '100%', maxWidth: 'none', objectFit: 'cover' }} />}
            </div>
            {/* Divider */}
            <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${pos}%`, width: 2, background: 'var(--nd-text-strong)', transform: 'translateX(-50%)', pointerEvents: 'none' }}>
              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 36, height: 36, borderRadius: '50%', background: 'var(--nd-text-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 16px rgba(0,0,0,0.35)' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2.5"><path d="M8 12h8M5 8l-4 4 4 4M19 8l4 4-4 4"/></svg>
              </div>
            </div>
            {/* BEFORE label — clickable */}
            <div
              onClick={(e) => { e.stopPropagation(); if (allBeforePhotos.length > 0) setLightbox({ images: allBeforePhotos, start: 0 }); }}
              style={{ position: 'absolute', top: 10, left: 10, background: 'rgba(0,0,0,0.65)', color: 'var(--nd-text-strong)', fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 5, letterSpacing: '0.08em', cursor: allBeforePhotos.length > 0 ? 'pointer' : 'default', zIndex: 2 }}
            >
              BEFORE {allBeforePhotos.length > 1 ? `(${allBeforePhotos.length})` : ''}
            </div>
            {/* AFTER label — clickable */}
            <div
              onClick={(e) => { e.stopPropagation(); if (allAfterPhotos.length > 0) setLightbox({ images: allAfterPhotos, start: 0 }); }}
              style={{ position: 'absolute', top: 10, right: 10, background: 'rgba(34,197,94,0.88)', color: 'var(--nd-text-strong)', fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 5, letterSpacing: '0.08em', cursor: allAfterPhotos.length > 0 ? 'pointer' : 'default', zIndex: 2 }}
            >
              AFTER {allAfterPhotos.length > 1 ? `(${allAfterPhotos.length})` : ''}
            </div>
          </>
        )}
      </div>

      {/* Timestamps */}
      {(beforeTs || afterTs) && (
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          {beforeTs && (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 7, background: isDark ? 'var(--nd-w05)' : '#F1F5F9', borderRadius: 8, padding: '7px 12px' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              <div>
                <div style={{ fontSize: 9, color: '#94A3B8', fontWeight: 700, letterSpacing: '0.06em' }}>BEFORE TAKEN</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#CBD5E1' : 'var(--nd-w12)', marginTop: 1 }}>{fmtDateTime(beforeTs)}</div>
              </div>
            </div>
          )}
          {afterTs && (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 7, background: 'rgba(34,197,94,0.07)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 8, padding: '7px 12px' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              <div>
                <div style={{ fontSize: 9, color: '#22C55E', fontWeight: 700, letterSpacing: '0.06em' }}>AFTER TAKEN</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#CBD5E1' : 'var(--nd-w12)', marginTop: 1 }}>{fmtDateTime(afterTs)}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {lightbox && (
        <Lightbox images={lightbox.images} startIndex={lightbox.start} onClose={() => setLightbox(null)}/>
      )}
    </div>
  );
};

// ─── DAILY BAR CHART ─────────────────────────────────────────────
const DailyBarChart = ({ dailyData, T, isDark, monthLabel }) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current || !dailyData || dailyData.length === 0) return;
    if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }
    const ctx = canvasRef.current.getContext('2d');
    chartRef.current = new window.Chart(ctx, {
      type: 'bar',
      data: {
        labels: dailyData.map(d => d.day),
        datasets: [
          {
            label: 'Completed',
            data: dailyData.map(d => d.completed),
            backgroundColor: 'rgba(34,197,94,0.85)',
            borderColor: 'rgba(34,197,94,1)',
            borderWidth: 0, borderRadius: 5, borderSkipped: false,
          },
          {
            label: 'Scheduled',
            data: dailyData.map(d => d.scheduled),
            backgroundColor: 'rgba(56,189,248,0.7)',
            borderColor: 'rgba(56,189,248,1)',
            borderWidth: 0, borderRadius: 5, borderSkipped: false,
          },
        ],
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: isDark ? 'var(--nd-surface-2)' : 'var(--nd-text-strong)',
            titleColor: isDark ? 'rgba(241,245,249,0.5)' : 'rgba(15,22,35,0.5)',
            bodyColor: isDark ? '#F1F5F9' : 'var(--nd-surface)',
            borderColor: isDark ? 'var(--nd-w12)' : 'rgba(0,0,0,0.1)',
            borderWidth: 1, padding: 12, cornerRadius: 10,
            boxWidth: 10, boxHeight: 10, boxPadding: 4, usePointStyle: false,
            titleFont: { family: 'Inter, sans-serif', size: 11, weight: '700' },
            bodyFont: { family: 'Inter, sans-serif', size: 13, weight: '600' },
            callbacks: {
              title: (items) => `${monthLabel} ${items[0].label}`,
              label: (item) => `  ${item.dataset.label}: ${item.parsed.y}`,
              afterBody: (items) => {
                const total = items.reduce((sum, i) => sum + i.parsed.y, 0);
                return total > 0 ? [`  Total: ${total}`] : [];
              },
            },
          },
        },
        scales: {
          x: {
            grid: { display: false }, border: { display: false },
            ticks: { color: isDark ? 'rgba(241,245,249,0.4)' : 'rgba(15,22,35,0.4)', font: { family: 'Inter, sans-serif', size: 10 }, maxRotation: 0, autoSkip: true, maxTicksLimit: 16 },
          },
          y: {
            beginAtZero: true,
            grid: { color: isDark ? 'var(--nd-w05)' : 'rgba(0,0,0,0.05)', drawTicks: false },
            border: { display: false, dash: [3, 3] },
            ticks: { color: isDark ? 'rgba(241,245,249,0.4)' : 'rgba(15,22,35,0.4)', font: { family: 'Inter, sans-serif', size: 10 }, stepSize: 1, padding: 8 },
          },
        },
      },
    });
    return () => { if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; } };
  }, [dailyData, isDark, monthLabel]);

  if (!dailyData || dailyData.length === 0) {
    return <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.muted, fontSize: 14 }}>No data for this month</div>;
  }
  return <div style={{ position: 'relative', height: 200 }}><canvas ref={canvasRef}/></div>;
};

// ─── GLOBAL CSS ───────────────────────────────────────────────────
const getCSS = (isDark) => `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif; background: ${isDark ? 'var(--nd-bg)' : '#F1F5F9'}; width: 100%; }
  ::-webkit-scrollbar { width: 5px; height: 5px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: ${isDark ? 'var(--nd-w12)' : 'rgba(0,0,0,0.15)'}; border-radius: 10px; }

  .cp-fade { animation: cpFade 0.4s ease both; }
  @keyframes cpFade { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }

  .cp-card {
    background: ${isDark ? 'var(--nd-surface)' : 'var(--nd-text-strong)'};
    border: 1px solid ${isDark ? 'var(--nd-w08)' : 'rgba(0,0,0,0.08)'};
    border-radius: 16px;
    transition: border-color 0.2s;
  }

  .cp-pill {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 5px 13px; border-radius: 20px; font-size: 11px; font-weight: 600;
    letter-spacing: 0.03em; white-space: nowrap;
  }

  .cp-tab {
    padding: 8px 22px; border-radius: 9px; border: none; cursor: pointer;
    font-size: 14px; font-weight: 600; font-family: 'Inter', sans-serif;
    transition: all 0.2s; letter-spacing: 0.01em;
  }
  .cp-tab.active { background: #38BDF8; color: #000; }
  .cp-tab.inactive { background: transparent; color: ${isDark ? 'rgba(241,245,249,0.5)' : 'rgba(15,22,35,0.5)'}; }
  .cp-tab.inactive:hover { color: ${isDark ? '#F1F5F9' : 'var(--nd-surface)'}; }

  .cp-filter {
    padding: 6px 16px; border-radius: 20px;
    border: 1px solid ${isDark ? 'var(--nd-w10)' : 'rgba(0,0,0,0.12)'};
    background: transparent; font-size: 12px; font-weight: 600; cursor: pointer;
    font-family: 'Inter', sans-serif; letter-spacing: 0.02em; transition: all 0.15s;
    color: ${isDark ? 'rgba(241,245,249,0.5)' : 'rgba(15,22,35,0.5)'};
  }
  .cp-filter.active { border-color: #38BDF8; background: rgba(56,189,248,0.1); color: #38BDF8; }
  .cp-filter:hover:not(.active) { border-color: ${isDark ? 'var(--nd-w20)' : 'rgba(0,0,0,0.2)'}; color: ${isDark ? '#F1F5F9' : 'var(--nd-surface)'}; }

  .cp-job-row {
    padding: 15px 22px;
    border-bottom: 1px solid ${isDark ? 'var(--nd-w05)' : 'rgba(0,0,0,0.06)'};
    cursor: pointer; transition: background 0.15s;
    display: grid; grid-template-columns: 1fr auto; gap: 12px; align-items: center;
  }
  .cp-job-row:hover { background: rgba(56,189,248,0.05); }
  .cp-job-row.selected { background: rgba(56,189,248,0.08); border-left: 3px solid #38BDF8; }

  .cp-site-card {
    background: ${isDark ? 'var(--nd-surface)' : 'var(--nd-text-strong)'};
    border: 1px solid ${isDark ? 'var(--nd-w08)' : 'rgba(0,0,0,0.08)'};
    border-radius: 14px; overflow: hidden; cursor: pointer; transition: all 0.2s;
  }
  .cp-site-card:hover { border-color: rgba(56,189,248,0.35); transform: translateY(-2px); }
  .cp-site-card.active { border-color: #38BDF8; }

  .cp-stat {
    background: ${isDark ? 'var(--nd-surface)' : 'var(--nd-text-strong)'};
    border: 1px solid ${isDark ? 'var(--nd-w08)' : 'rgba(0,0,0,0.08)'};
    border-radius: 16px; padding: 22px 24px; overflow: hidden;
  }

  .cp-btn { padding: 9px 20px; border-radius: 9px; border: none; cursor: pointer; font-size: 13px; font-weight: 600; font-family: 'Inter', sans-serif; transition: all 0.2s; }
  .cp-btn-primary { background: #38BDF8; color: #000; }
  .cp-btn-primary:hover { background: #7DD3FA; }
  .cp-btn-ghost { background: ${isDark ? 'var(--nd-w06)' : 'rgba(0,0,0,0.05)'}; color: ${isDark ? 'rgba(241,245,249,0.6)' : 'rgba(15,22,35,0.6)'}; }
  .cp-btn-ghost:hover { background: ${isDark ? 'var(--nd-w10)' : 'rgba(0,0,0,0.09)'}; color: ${isDark ? '#F1F5F9' : 'var(--nd-surface)'}; }

  .cp-input {
    width: 100%; padding: 10px 16px; border-radius: 10px;
    border: 1px solid ${isDark ? 'var(--nd-w08)' : 'rgba(0,0,0,0.1)'};
    background: ${isDark ? 'var(--nd-surface)' : '#F8FAFC'};
    color: ${isDark ? '#F1F5F9' : 'var(--nd-surface)'}; font-size: 14px;
    font-family: 'Inter', sans-serif; outline: none;
  }
  .cp-input:focus { border-color: rgba(56,189,248,0.45); }
  .cp-input::placeholder { color: ${isDark ? 'rgba(241,245,249,0.25)' : 'rgba(15,22,35,0.3)'}; }

  @keyframes spin { to { transform: rotate(360deg); } }
  .cp-spin { animation: spin 0.75s linear infinite; }

  .cp-alert { display: flex; align-items: flex-start; gap: 10px; padding: 12px 16px; border-radius: 10px; margin-bottom: 8px; }
  .cp-alert-warn { background: rgba(245,158,11,0.08); border: 1px solid rgba(245,158,11,0.2); }

  .theme-toggle {
    width: 40px; height: 40px; border-radius: 10px;
    border: 1px solid ${isDark ? 'var(--nd-w10)' : 'rgba(0,0,0,0.12)'};
    background: ${isDark ? 'var(--nd-w05)' : 'rgba(0,0,0,0.04)'};
    color: ${isDark ? '#F1F5F9' : 'var(--nd-surface)'}; cursor: pointer;
    display: flex; align-items: center; justify-content: center; transition: all 0.2s;
  }
  .theme-toggle:hover { border-color: #38BDF8; color: #38BDF8; }

  .photo-thumb {
    cursor: pointer; transition: transform 0.15s, box-shadow 0.15s;
  }
  .photo-thumb:hover { transform: scale(1.04); box-shadow: 0 4px 20px rgba(0,0,0,0.35); }
`;

// ═══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════
const CustomerPortal = () => {
  const [isDark, setIsDark]           = useState(true);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [profile, setProfile]         = useState(null);
  const [stats, setStats]             = useState(null);
  const [allJobs, setAllJobs]         = useState([]);
  const [activeTab, setActiveTab]     = useState('overview');
  const [selectedJob, setSelectedJob] = useState(null);
  const [activeSite, setActiveSite]   = useState(null);
  const [filter, setFilter]           = useState('all');
  const [search, setSearch]           = useState('');
  const [todayJobs, setTodayJobs]     = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const nowDate = new Date();
  const defaultMonth = `${nowDate.getFullYear()}-${String(nowDate.getMonth() + 1).padStart(2, '0')}`;
  const [chartMonth, setChartMonth] = useState(defaultMonth);

  const T = {
    bg:      isDark ? 'var(--nd-bg)' : '#F1F5F9',
    surface: isDark ? 'var(--nd-bg)' : 'var(--nd-text-strong)',
    card:    isDark ? 'var(--nd-surface)' : 'var(--nd-text-strong)',
    border:  isDark ? 'var(--nd-w08)' : 'rgba(0,0,0,0.08)',
    text:    isDark ? '#F1F5F9' : 'var(--nd-surface)',
    muted:   isDark ? 'rgba(241,245,249,0.45)' : 'rgba(15,22,35,0.48)',
    accent:  '#38BDF8',
    green:   '#22C55E',
    amber:   '#F59E0B',
    red:     '#F87171',
    purple:  '#A78BFA',
    orange:  '#FB923C',
  };

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      setLoading(true); setError(null);
      const cfg = getAuthHeader();
      const [profRes, statsRes, commRes, maintRes, todayRes] = await Promise.all([
        axios.get(`${API}/customer-portal/customer/profile`, cfg).catch(() => ({ data: {} })),
        axios.get(`${API}/customer-portal/stats`, cfg).catch(() => ({ data: {} })),
        axios.get(`${API}/customer-portal/history`, cfg).catch(() => ({ data: { jobs: [] } })),
        axios.get(`${API}/customer-portal/maintenance-history`, cfg).catch(() => ({ data: { jobs: [] } })),
        axios.get(`${API}/customer-portal/today`, cfg).catch(() => ({ data: { jobs: [] } })),
      ]);

      const prof      = profRes.data?.customer || null;
      const st        = statsRes.data || {};
      const commJobs  = (commRes.data?.jobs  || []).map(j => ({ ...j, _type: 'commercial' }));
      const maintJobs = (maintRes.data?.jobs || []).map(j => ({ ...j, _type: 'maintenance' }));
      const combined  = [...commJobs, ...maintJobs].sort(
        (a, b) => new Date(b.scheduled_date) - new Date(a.scheduled_date)
      );

      setProfile(prof);
      setStats(st);
      setAllJobs(combined);
      setTodayJobs(todayRes.data?.jobs || []);

      // Gallery default: only completed jobs with photos
      const withPhotos = combined.find(j =>
        j.status === 'completed' && (
          parsePhotos(j.before_photos || j.before_photo_url).length > 0 ||
          parsePhotos(j.after_photos  || j.after_photo_url).length > 0
        )
      );
      if (withPhotos) setActiveSite(withPhotos);
    } catch {
      setError('Unable to load portal data.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectJob = async (j) => {
    setSelectedJob(j);
    setDetailLoading(true);
    try {
      const cfg = getAuthHeader();
      const scheduleId = j.schedule_id || j.id;
      const res = await axios.get(`${API}/customer-portal/${scheduleId}/detail`, cfg);
      if (res.data?.job) setSelectedJob({ ...res.data.job, _type: j._type });
    } catch {
      // keep list data if detail fetch fails
    } finally {
      setDetailLoading(false);
    }
  };

  // Derived
  const completedJobs   = allJobs.filter(j => j.status === 'completed');
  const systemSize      = parseFloat(profile?.total_system_size || 0);
  const totalPanels     = parseInt(profile?.total_panel_count || 0);
  const avgServiceTime  = calcAvgServiceTime(allJobs);
  const panelsThisMonth = calcPanelsThisMonth(allJobs);

  const availableMonths = [...new Set(
    allJobs.map(j => {
      const d = new Date(j.scheduled_date);
      return isNaN(d) ? null : `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
    }).filter(Boolean)
  )].sort((a, b) => b.localeCompare(a));

  const dailyData  = buildDailyData(allJobs, chartMonth);
  const monthLabel = chartMonth ? new Date(chartMonth + '-01').toLocaleString('default', { month: 'long', year: 'numeric' }) : '';
  const monthShort = chartMonth ? new Date(chartMonth + '-01').toLocaleString('default', { month: 'short' }) : '';

  const filteredJobs = allJobs.filter(j => {
    if (j.status !== 'completed') return false;
    if (filter !== 'all' && j._type !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!j.site_name?.toLowerCase().includes(q) && !j.team_lead_name?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  // ── GALLERY: ONLY completed jobs with photos ──
  const jobsWithPhotos = allJobs.filter(j =>
    j.status === 'completed' && (
      parsePhotos(j.before_photos || j.before_photo_url).length > 0 ||
      parsePhotos(j.after_photos  || j.after_photo_url).length > 0
    )
  ).slice(0, 18);

  const upcomingJobs = allJobs.filter(j => j.status === 'scheduled').slice(0, 4);
  const alertJobs    = allJobs.filter(j => j.status === 'rescheduled').slice(0, 3);

  const nowDate2 = new Date();
  const currentMonthJobs = allJobs.filter(j => {
    const d = new Date(j.scheduled_date);
    return d.getMonth() === nowDate2.getMonth() && d.getFullYear() === nowDate2.getFullYear();
  });
  const currentMonthCompleted = currentMonthJobs.filter(j => j.status === 'completed').length;
  const currentMonthCompletionPct = currentMonthJobs.length
    ? Math.round((currentMonthCompleted / currentMonthJobs.length) * 100)
    : 0;

  const currentMonthApproved = allJobs.filter(j => {
    const d = new Date(j.scheduled_date);
    return j.status === 'completed' &&
      d.getMonth() === nowDate2.getMonth() &&
      d.getFullYear() === nowDate2.getFullYear();
  }).length;

  // ── Loading / Error ──────────────────────────────────────────────
  if (loading) return (
    <>
      <style>{getCSS(isDark)}</style>
      <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
        <div className="cp-spin" style={{ width: 40, height: 40, border: `2px solid ${T.border}`, borderTopColor: T.accent, borderRadius: '50%' }}/>
        <p style={{ color: T.muted, fontSize: 13, letterSpacing: '0.12em' }}>LOADING PORTAL…</p>
      </div>
    </>
  );

  if (error) return (
    <>
      <style>{getCSS(isDark)}</style>
      <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
        <p style={{ color: T.red, fontSize: 15 }}>{error}</p>
        <button className="cp-btn cp-btn-primary" onClick={fetchAll}>Retry</button>
      </div>
    </>
  );

  return (
    <>
      <style>{getCSS(isDark)}</style>
      <div style={{ minHeight: '100vh', background: T.bg, color: T.text, fontFamily: "'Inter', sans-serif", width: '100%' }}>

        {/* ━━━━ HEADER ━━━━ */}
        <div style={{ background: T.surface, borderBottom: `1px solid ${T.border}`, padding: '0 32px', position: 'sticky', top: 0, zIndex: 100, boxShadow: isDark ? '0 1px 0 var(--nd-w04)' : '0 1px 8px rgba(0,0,0,0.06)' }}>
          <div style={{ maxWidth: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 64 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ width: 38, height: 38, borderRadius: 9, background: `linear-gradient(135deg, ${T.accent}, ${T.green})`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2.2">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                </svg>
              </div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.1 }}>{profile?.client_name || 'Customer Portal'}</div>
                <div style={{ fontSize: 10, color: T.muted, letterSpacing: '0.1em', marginTop: 2 }}>SOLAR OPERATIONS DASHBOARD</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ display: 'flex', gap: 3, background: isDark ? 'var(--nd-w05)' : 'rgba(0,0,0,0.05)', borderRadius: 11, padding: 4 }}>
                {[['overview','Overview'],['gallery','Media Gallery'],['history','Service History']].map(([tab, lbl]) => (
                  <button key={tab} className={`cp-tab ${activeTab === tab ? 'active' : 'inactive'}`} onClick={() => setActiveTab(tab)}>{lbl}</button>
                ))}
              </div>
              <button className="theme-toggle" onClick={() => setIsDark(d => !d)} title="Toggle theme">
                <ThemeIcon isDark={isDark}/>
              </button>
            </div>
          </div>
        </div>

        {/* ━━━━ CONTENT ━━━━ */}
        <div style={{ width: '100%', padding: '28px 32px' }}>

          {/* ══════════ OVERVIEW ══════════ */}
          {activeTab === 'overview' && (
            <div className="cp-fade">
              {profile && (
                <div style={{
                  background: isDark ? 'linear-gradient(120deg,var(--nd-surface) 0%,var(--nd-surface) 100%)' : 'linear-gradient(120deg,#EFF6FF 0%,#F0F9FF 100%)',
                  border: `1px solid rgba(56,189,248,0.2)`, borderRadius: 18, padding: '22px 28px', marginBottom: 24,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16,
                }}>
                  <div>
                    <div style={{ fontSize: 12, color: T.muted, marginBottom: 5, letterSpacing: '0.1em', fontWeight: 600 }}>WELCOME BACK</div>
                    <div style={{ fontSize: 32, fontWeight: 900, letterSpacing: '-0.03em', lineHeight: 1 }}>{profile.client_name}</div>
                    <div style={{ fontSize: 13, color: T.muted, marginTop: 6 }}>
                      {profile.contact_person && <span style={{ marginRight: 20 }}>POC: <strong style={{ color: T.text }}>{profile.contact_person}</strong></span>}
                      {profile.contract_type && <span style={{ marginRight: 20 }}>Contract: <strong style={{ color: T.text }}>{profile.contract_type}</strong></span>}
                      {profile.contractual_services_count && <span>Jobs: <strong style={{ color: T.text }}>{profile.contractual_services_count}</strong></span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    {systemSize > 0 && (
                      <div style={{ background: 'rgba(56,189,248,0.09)', border: '1px solid rgba(56,189,248,0.22)', borderRadius: 12, padding: '12px 20px', textAlign: 'center', minWidth: 100 }}>
                        <div style={{ fontSize: 26, fontWeight: 800, color: T.accent, letterSpacing: '-0.02em' }}>{systemSize}<span style={{ fontSize: 13, fontWeight: 600 }}> kW</span></div>
                        <div style={{ fontSize: 10, color: T.muted, letterSpacing: '0.1em', marginTop: 3, fontWeight: 600 }}>SYSTEM SIZE</div>
                      </div>
                    )}
                    {totalPanels > 0 && (
                      <div style={{ background: 'rgba(34,197,94,0.09)', border: '1px solid rgba(34,197,94,0.22)', borderRadius: 12, padding: '12px 20px', textAlign: 'center', minWidth: 100 }}>
                        <div style={{ fontSize: 26, fontWeight: 800, color: T.green, letterSpacing: '-0.02em' }}>{totalPanels.toLocaleString()}</div>
                        <div style={{ fontSize: 10, color: T.muted, letterSpacing: '0.1em', marginTop: 3, fontWeight: 600 }}>TOTAL PANELS</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* KPI Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 24 }}>
                {[
                  { label: 'SCHEDULED THIS MONTH', value: currentMonthApproved, sub: new Date().toLocaleString('default', { month: 'long', year: 'numeric' }), color: T.accent },
                  { label: 'COMPLETED THIS MONTH', value: allJobs.filter(j => { const d = new Date(j.scheduled_date); const n = new Date(); return j.status === 'completed' && d.getMonth() === n.getMonth() && d.getFullYear() === n.getFullYear(); }).length, sub: new Date().toLocaleString('default', { month: 'long' }), color: T.green },
                  { label: 'AVG SERVICE TIME', value: avgServiceTime || '—', sub: 'Start → complete', color: T.purple },
                  { label: 'ASSIGNED JOB COMPLETION %', value: `${currentMonthCompletionPct}%`, sub: `${currentMonthCompleted} of ${currentMonthJobs.length} this month`, color: T.amber },
                ].map((s, i) => (
                  <div key={i} className="cp-stat" style={{ borderTop: `3px solid ${s.color}` }}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: T.muted, marginBottom: 10 }}>{s.label}</div>
                    <div style={{ fontSize: i === 3 ? 28 : 38, fontWeight: 800, color: T.text, letterSpacing: '-0.03em', lineHeight: 1 }}>{s.value}</div>
                    <div style={{ fontSize: 12, color: T.muted, marginTop: 7, fontWeight: 500 }}>{s.sub}</div>
                  </div>
                ))}
              </div>

              {/* Chart + Right Panel */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 18, marginBottom: 20 }}>
                <div className="cp-card" style={{ padding: '22px 26px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700 }}>Daily Service Activity — {monthLabel}</div>
                      <div style={{ fontSize: 12, color: T.muted, marginTop: 3 }}>Completed & scheduled jobs per day</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                      <div style={{ display: 'flex', gap: 16, fontSize: 12, color: T.muted, alignItems: 'center' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          <span style={{ width: 20, height: 2.5, background: T.green, display: 'inline-block', borderRadius: 2 }}/>Completed
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          <span style={{ width: 20, height: 2.5, background: T.accent, display: 'inline-block', borderRadius: 2, opacity: 0.7 }}/>Scheduled
                        </span>
                      </div>
                      <select value={chartMonth} onChange={e => setChartMonth(e.target.value)}
                        style={{ padding: '6px 12px', borderRadius: 9, border: `1px solid ${T.border}`, background: isDark ? 'var(--nd-bg)' : '#F8FAFC', color: T.text, fontSize: 12, fontFamily: 'Inter,sans-serif', cursor: 'pointer', outline: 'none', fontWeight: 500 }}>
                        {availableMonths.map(m => (
                          <option key={m} value={m}>{new Date(m + '-01').toLocaleString('default', { month: 'long', year: 'numeric' })}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <DailyBarChart dailyData={dailyData} T={T} isDark={isDark} monthLabel={monthShort}/>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div className="cp-card" style={{ padding: '18px 20px', flex: 1 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', color: T.muted, marginBottom: 14 }}>UPCOMING SCHEDULE</div>
                    {upcomingJobs.length === 0 ? (
                      <div style={{ fontSize: 13, color: T.muted, textAlign: 'center', padding: '14px 0' }}>No upcoming services</div>
                    ) : upcomingJobs.map((j, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: i < upcomingJobs.length - 1 ? `1px solid ${T.border}` : 'none' }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>{j.site_name}</div>
                          <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{fmtDate(j.scheduled_date)}</div>
                        </div>
                        <span className="cp-pill" style={{ background: '#38BDF818', color: T.accent }}>#{j.service_number}</span>
                      </div>
                    ))}
                  </div>

                  <div className="cp-card" style={{ padding: '18px 20px', flex: 1 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', color: T.muted, marginBottom: 14 }}>ACTIVE ALERTS</div>
                    {alertJobs.length === 0 ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 9, height: 9, borderRadius: '50%', background: T.green }}/>
                        <span style={{ fontSize: 13, color: T.green, fontWeight: 500 }}>All systems normal</span>
                      </div>
                    ) : alertJobs.map((j, i) => (
                      <div key={i} className="cp-alert cp-alert-warn" style={{ marginBottom: i < alertJobs.length - 1 ? 8 : 0 }}>
                        <div style={{ width: 7, height: 7, borderRadius: '50%', background: T.amber, flexShrink: 0, marginTop: 4 }}/>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>{j.site_name}</div>
                          <div style={{ fontSize: 11, color: T.muted }}>Rescheduled · {fmtDate(j.scheduled_date)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Service Proof + Recent Jobs */}
              <div style={{ display: 'grid', gridTemplateColumns: '440px 1fr', gap: 18 }}>
                <div className="cp-card" style={{ padding: '20px 22px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700 }}>Today's Service</div>
                      <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{new Date().toLocaleDateString('en-PK', { weekday: 'long', day: 'numeric', month: 'long' })}</div>
                    </div>
                    {todayJobs.length > 0 && (
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#22C55E', boxShadow: '0 0 0 3px rgba(34,197,94,0.2)' }}/>
                    )}
                  </div>
                  {todayJobs.length === 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '32px 0', gap: 10 }}>
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="1.5"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                      <div style={{ fontSize: 13, color: T.muted, fontWeight: 500 }}>No service scheduled for today</div>
                    </div>
                  ) : todayJobs.map((job, idx) => {
                    let stage = 'Scheduled', stageColor = T.accent;
                    if (job.completed_at)             { stage = 'Completed';            stageColor = '#22C55E'; }
                    else if (job.after_photos_at)     { stage = 'After Photos Taken';   stageColor = '#A78BFA'; }
                    else if (job.before_photos_at)    { stage = 'Cleaning In Progress'; stageColor = '#F59E0B'; }
                    else if (job.started_at)          { stage = 'Work Started';          stageColor = '#F59E0B'; }
                    else if (job.reached_location_at) { stage = 'Team Arrived';          stageColor = '#38BDF8'; }
                    const timeline = [
                      { label: 'Shift Start',  ts: null, always: true, note: job.shift ? job.shift.charAt(0).toUpperCase() + job.shift.slice(1) : '—' },
                      { label: 'Team Arrived', ts: job.reached_location_at, always: false },
                      { label: 'Work Started', ts: job.started_at, always: false },
                      { label: 'Completed',    ts: job.completed_at, always: false },
                    ].filter(e => e.always || e.ts);
                    return (
                      <div key={idx} style={{ marginBottom: idx < todayJobs.length - 1 ? 20 : 0, paddingBottom: idx < todayJobs.length - 1 ? 20 : 0, borderBottom: idx < todayJobs.length - 1 ? `1px solid ${T.border}` : 'none' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 700 }}>{job.site_name}</div>
                            <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>Service #{job.service_number}</div>
                          </div>
                          <span style={{ fontSize: 11, fontWeight: 700, color: stageColor, background: `${stageColor}18`, border: `1px solid ${stageColor}30`, padding: '4px 10px', borderRadius: 20, whiteSpace: 'nowrap' }}>{stage}</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
                          {timeline.map((e, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <div style={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0, background: e.ts ? '#22C55E' : T.border, border: `1.5px solid ${e.ts ? '#22C55E' : T.border}` }}/>
                              <span style={{ fontSize: 12, color: T.muted, width: 110, flexShrink: 0 }}>{e.label}</span>
                              <span style={{ fontSize: 12, fontWeight: 600, color: e.ts ? T.text : T.muted }}>{e.ts ? fmtTime(e.ts) : (e.note || '—')}</span>
                            </div>
                          ))}
                        </div>
                        {job.team_members && job.team_members.length > 0 && (
                          <div>
                            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: T.muted, marginBottom: 8 }}>TEAM</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                              {job.team_members.map((m, mi) => (
                                <div key={mi} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <div style={{ width: 26, height: 26, borderRadius: '50%', background: m.is_lead ? 'rgba(56,189,248,0.15)' : T.border, border: `1.5px solid ${m.is_lead ? T.accent : T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: m.is_lead ? T.accent : T.muted, flexShrink: 0 }}>
                                    {(m.name || '?').charAt(0).toUpperCase()}
                                  </div>
                                  <div>
                                    <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{m.name}</span>
                                    {m.is_lead && <span style={{ fontSize: 10, color: T.accent, marginLeft: 6, fontWeight: 600 }}>Lead</span>}
                                    {m.designation && <span style={{ fontSize: 11, color: T.muted, marginLeft: 6 }}>{m.designation}</span>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Recent Jobs */}
                <div className="cp-card" style={{ overflow: 'hidden' }}>
                  <div style={{ padding: '16px 22px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 15, fontWeight: 700 }}>Recent Services</span>
                    <button className="cp-btn cp-btn-ghost" onClick={() => setActiveTab('history')} style={{ fontSize: 12 }}>View all →</button>
                  </div>
                  {allJobs.filter(j => j.status === 'completed').slice(0, 7).map((j, i) => {
                    const sm = statusMeta(j.status);
                    return (
                      <div key={i} className="cp-job-row" onClick={() => { handleSelectJob(j); setActiveTab('history'); }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 3 }}>{j.site_name}</div>
                          <div style={{ fontSize: 11, color: T.muted }}>{fmtDate(j.scheduled_date)} · #{j.service_number} · {j.team_lead_name || '—'}</div>
                        </div>
                        <span className="cp-pill" style={{ background: sm.bg, color: sm.color }}>{sm.label}</span>
                      </div>
                    );
                  })}
                  {allJobs.filter(j => j.status === 'completed').length === 0 && (
                    <div style={{ padding: 28, textAlign: 'center', color: T.muted, fontSize: 13 }}>No approved services yet</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ══════════ GALLERY TAB ══════════ */}
          {activeTab === 'gallery' && (
            <div className="cp-fade">
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 5 }}>Media Verification Gallery</div>
                <div style={{ fontSize: 14, color: T.muted }}>
                  Completed services only — drag slider to compare · click any photo to enlarge
                </div>
              </div>

              {/* Empty state */}
              {jobsWithPhotos.length === 0 && (
                <div style={{ textAlign: 'center', padding: 80, color: T.muted, fontSize: 14 }}>
                  No photos for completed services yet
                </div>
              )}

              {activeSite && (() => {
                const b   = parsePhotos(activeSite.before_photos || activeSite.before_photo_url);
                const a   = parsePhotos(activeSite.after_photos  || activeSite.after_photo_url);
                const bTs = activeSite.before_photos_at || activeSite.before_photo_at;
                const aTs = activeSite.after_photos_at  || activeSite.after_photo_at;
                const sm  = statusMeta(activeSite.status);
                return (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 18, marginBottom: 22 }}>
                    <div className="cp-card" style={{ padding: 22 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                        <div>
                          <div style={{ fontSize: 16, fontWeight: 700 }}>{activeSite.site_name}</div>
                          <div style={{ fontSize: 12, color: T.muted, marginTop: 3 }}>{fmtDate(activeSite.scheduled_date)} · Service #{activeSite.service_number}</div>
                        </div>
                        <span className="cp-pill" style={{ background: sm.bg, color: sm.color }}>{sm.label}</span>
                      </div>
                      <BeforeAfterSlider
                        before={b[0]} after={a[0]} height={280} isDark={isDark}
                        beforeTs={bTs} afterTs={aTs}
                        beforePhotos={b} afterPhotos={a}
                      />
                    </div>

                    {/* Side panel with clickable thumbnails */}
                    <div className="cp-card" style={{ padding: 18, overflowY: 'auto', maxHeight: 420 }}>
                      <GalleryPhotoPanel before={b} after={a} isDark={isDark} T={T}/>
                    </div>
                  </div>
                );
              })()}

              {/* Job cards grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
                {jobsWithPhotos.map((j, i) => {
                  const b  = parsePhotos(j.before_photos || j.before_photo_url);
                  const a  = parsePhotos(j.after_photos  || j.after_photo_url);
                  const sm = statusMeta(j.status);
                  const isA = activeSite?.schedule_id === j.schedule_id || activeSite?.id === j.id;
                  return (
                    <div key={i} className={`cp-site-card ${isA ? 'active' : ''}`} onClick={() => setActiveSite(j)}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', height: 100, overflow: 'hidden' }}>
                        <div style={{ position: 'relative', overflow: 'hidden' }}>
                          {b[0] ? <img src={photoUrl(b[0])} alt="before" style={{ width: '100%', height: '100%', objectFit: 'cover' }}/> : <div style={{ width: '100%', height: '100%', background: isDark ? 'var(--nd-surface-2)' : 'var(--nd-text)' }}/>}
                          <div style={{ position: 'absolute', bottom: 5, left: 5, fontSize: 9, fontWeight: 700, background: 'rgba(0,0,0,0.7)', color: 'var(--nd-text-strong)', padding: '2px 6px', borderRadius: 4 }}>BEFORE</div>
                        </div>
                        <div style={{ position: 'relative', overflow: 'hidden' }}>
                          {a[0] ? <img src={photoUrl(a[0])} alt="after" style={{ width: '100%', height: '100%', objectFit: 'cover' }}/> : <div style={{ width: '100%', height: '100%', background: isDark ? 'var(--nd-surface-2)' : 'var(--nd-text)' }}/>}
                          <div style={{ position: 'absolute', bottom: 5, right: 5, fontSize: 9, fontWeight: 700, background: 'rgba(34,197,94,0.85)', color: 'var(--nd-text-strong)', padding: '2px 6px', borderRadius: 4 }}>AFTER</div>
                        </div>
                      </div>
                      <div style={{ padding: '12px 14px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ fontSize: 13, fontWeight: 600, flex: 1, marginRight: 8 }}>{j.site_name}</div>
                          <span className="cp-pill" style={{ background: sm.bg, color: sm.color, fontSize: 9 }}>{sm.label}</span>
                        </div>
                        <div style={{ fontSize: 11, color: T.muted, marginTop: 4 }}>{fmtDate(j.scheduled_date)} · {j.team_lead_name || '—'}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ══════════ HISTORY TAB ══════════ */}
          {activeTab === 'history' && (
            <div className="cp-fade" style={{ display: 'grid', gridTemplateColumns: selectedJob ? '1fr 420px' : '1fr', gap: 20 }}>

              {/* List */}
              <div>
                <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                  <input className="cp-input" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search site or team lead…" style={{ flex: 1, minWidth: 200 }}/>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {[['all','All'],['commercial','Cleaning'],['maintenance','Maintenance']].map(([v,l]) => (
                      <button key={v} className={`cp-filter ${filter === v ? 'active' : ''}`} onClick={() => setFilter(v)}>{l}</button>
                    ))}
                  </div>
                </div>

                <div className="cp-card" style={{ overflow: 'hidden' }}>
                  <div style={{ padding: '13px 22px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 12, color: T.muted, fontWeight: 700, letterSpacing: '0.08em' }}>{filteredJobs.length} SERVICES</span>
                    {selectedJob && <button className="cp-btn cp-btn-ghost" style={{ fontSize: 12 }} onClick={() => setSelectedJob(null)}>Close ×</button>}
                  </div>
                  {filteredJobs.length === 0 ? (
                    <div style={{ padding: 40, textAlign: 'center', color: T.muted, fontSize: 13 }}>No services match your filters.</div>
                  ) : filteredJobs.map((j, i) => {
                    const sm  = statusMeta(j.status);
                    const hp  = parsePhotos(j.before_photos || j.before_photo_url).length > 0 ||
                                parsePhotos(j.after_photos  || j.after_photo_url).length > 0;
                    const sel = selectedJob?.schedule_id === j.schedule_id || selectedJob?.id === j.id;
                    return (
                      <div key={i} className={`cp-job-row ${sel ? 'selected' : ''}`} onClick={() => handleSelectJob(j)}>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 3 }}>{j.site_name}</div>
                          <div style={{ fontSize: 12, color: T.muted }}>{fmtDate(j.scheduled_date)} · #{j.service_number} · {j.team_lead_name || '—'}{j._type === 'maintenance' ? ' · Maintenance' : ''}</div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          {hp && <span style={{ fontSize: 12, color: T.accent }}>📷</span>}
                          <span className="cp-pill" style={{ background: sm.bg, color: sm.color }}>{sm.label}</span>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Detail Panel */}
              {selectedJob && (() => {
                const sm  = statusMeta(selectedJob.status);
                const b   = parsePhotos(selectedJob.before_photos || selectedJob.before_photo_url);
                const a   = parsePhotos(selectedJob.after_photos  || selectedJob.after_photo_url);
                const tv  = parsePhotos(selectedJob.temp_voltage_photos);
                const bTs = selectedJob.before_photos_at || selectedJob.before_photo_at;
                const aTs = selectedJob.after_photos_at  || selectedJob.after_photo_at;

                const Row = ({ label, value }) => value != null && value !== '' ? (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: `1px solid ${T.border}`, fontSize: 13 }}>
                    <span style={{ color: T.muted, fontWeight: 500 }}>{label}</span>
                    <span style={{ color: T.text, fontWeight: 600, textAlign: 'right', maxWidth: '60%' }}>{value}</span>
                  </div>
                ) : null;

                const BoolRow = ({ label, value }) => value != null ? (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: `1px solid ${T.border}`, fontSize: 13 }}>
                    <span style={{ color: T.muted, fontWeight: 500 }}>{label}</span>
                    <span style={{ fontWeight: 700, color: value === true || value === 'Yes' ? '#22C55E' : '#F87171' }}>
                      {value === true || value === 'Yes' ? '✓ Yes' : '✕ No'}
                    </span>
                  </div>
                ) : null;

                const SectionHead = ({ label, color }) => (
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: color || T.muted, marginBottom: 8, marginTop: 18, paddingBottom: 6, borderBottom: `1px solid ${T.border}` }}>
                    {label}
                  </div>
                );

                return (
                  <div className="cp-card" style={{ padding: 22, position: 'sticky', top: 76, maxHeight: 'calc(100vh - 108px)', overflowY: 'auto' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                      <div>
                        <div style={{ fontSize: 15, fontWeight: 700 }}>{selectedJob.site_name}</div>
                        <span className="cp-pill" style={{ background: sm.bg, color: sm.color, marginTop: 6, display: 'inline-flex' }}>{sm.label}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        {detailLoading && <div className="cp-spin" style={{ width: 16, height: 16, border: `2px solid ${T.border}`, borderTopColor: T.accent, borderRadius: '50%' }}/>}
                        <button onClick={() => setSelectedJob(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.muted, padding: 4 }}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
                        </button>
                      </div>
                    </div>

                    {(b[0] || a[0]) && (
                      <div style={{ marginBottom: 16 }}>
                        <BeforeAfterSlider
                          before={b[0]} after={a[0]} height={180} isDark={isDark}
                          beforeTs={bTs} afterTs={aTs}
                          beforePhotos={b} afterPhotos={a}
                        />
                      </div>
                    )}

                    <SectionHead label="JOB INFORMATION"/>
                    <Row label="Date"           value={fmtDate(selectedJob.scheduled_date)}/>
                    <Row label="Service #"      value={selectedJob.service_number}/>
                    <Row label="Team Lead"      value={selectedJob.team_lead_name}/>
                    <Row label="System Size"    value={selectedJob.system_size ? `${selectedJob.system_size} kW` : null}/>
                    <Row label="System Type"    value={selectedJob.system_type !== 'N/A' ? selectedJob.system_type : null}/>
                    <Row label="Panels Cleaned" value={selectedJob.total_panels_cleaned || selectedJob.fsr_panels_cleaned || null}/>
                    <Row label="Notes"          value={selectedJob.notes || selectedJob.remarks}/>

                    <SectionHead label="TIMELINE"/>
                    <Row label="Team Arrived"  value={selectedJob.reached_location_at ? fmtDateTime(selectedJob.reached_location_at) : null}/>
                    <Row label="Work Started"  value={selectedJob.started_at   ? fmtDateTime(selectedJob.started_at)   : null}/>
                    <Row label="Before Photos" value={bTs ? fmtDateTime(bTs) : null}/>
                    <Row label="After Photos"  value={aTs ? fmtDateTime(aTs) : null}/>
                    <Row label="Completed"     value={selectedJob.completed_at ? fmtDateTime(selectedJob.completed_at) : null}/>

                    {(selectedJob.cable_condition || selectedJob.panel_damage || selectedJob.inverter_alarm) && (<>
                      <SectionHead label="FIELD SERVICE REPORT" color={T.accent}/>
                      <Row label="Cable Condition"   value={selectedJob.cable_condition}/>
                      <Row label="Cable Quantity"    value={selectedJob.cable_quantity}/>
                      <Row label="Panel Damage"      value={selectedJob.panel_damage}/>
                      <Row label="Panel Brand"       value={selectedJob.panel_brand}/>
                      <Row label="Inverter Alarm"    value={selectedJob.inverter_alarm}/>
                      <Row label="Alarm Code"        value={selectedJob.alarm_code}/>
                      <Row label="Potential Shading" value={selectedJob.potential_shading}/>
                      <Row label="Shading Details"   value={selectedJob.shading_details}/>
                      <BoolRow label="Rusting"        value={selectedJob.rusting}/>
                      <BoolRow label="Bird Dropping"  value={selectedJob.bird_dropping}/>
                      <BoolRow label="Moss & Debris"  value={selectedJob.mos_and_debris}/>
                      <BoolRow label="Earthing"       value={selectedJob.earthing}/>
                      <BoolRow label="Cash Collected" value={selectedJob.cash_collected}/>
                      <Row label="Additional Notes"  value={selectedJob.additional_notes}/>
                    </>)}

                    {selectedJob.customer_signature && (<>
                      <SectionHead label="CUSTOMER SIGNATURE"/>
                      <img src={selectedJob.customer_signature.startsWith('http') ? selectedJob.customer_signature : `${IMG_BASE}/uploads/signatures/${selectedJob.customer_signature}`}
                        alt="Signature" style={{ width: '100%', maxWidth: 260, height: 80, objectFit: 'contain', borderRadius: 8, border: `1px solid ${T.border}`, background: 'white', display: 'block' }}/>
                    </>)}

                    {/* ── Clickable photo grids ── */}
                    {b.length > 0 && (
                      <ClickablePhotoGrid
                        photos={b}
                        label="BEFORE PHOTOS"
                        labelColor={T.muted}
                        borderColor={T.border}
                        isDark={isDark}
                        T={T}
                      />
                    )}

                    {a.length > 0 && (
                      <ClickablePhotoGrid
                        photos={a}
                        label="AFTER PHOTOS"
                        labelColor={T.green}
                        borderColor="rgba(34,197,94,0.25)"
                        isDark={isDark}
                        T={T}
                      />
                    )}

                    {tv.length > 0 && (
                      <ClickablePhotoGrid
                        photos={tv}
                        label="TOOL BOX TALK"
                        labelColor={T.amber}
                        borderColor="rgba(245,158,11,0.3)"
                        isDark={isDark}
                        T={T}
                      />
                    )}
                  </div>
                );
              })()}
            </div>
          )}

        </div>
      </div>
    </>
  );
};

// ─── GALLERY SIDE PANEL (clickable thumbnails) ────────────────────
const GalleryPhotoPanel = ({ before, after, isDark, T }) => {
  const [lightbox, setLightbox] = useState(null);

  return (
    <>
      {before.length > 0 && (
        <>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: T.muted, marginBottom: 10 }}>BEFORE ({before.length})</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 6, marginBottom: 14 }}>
            {before.map((p, i) => (
              <div
                key={i}
                onClick={() => setLightbox({ images: before, start: i })}
                style={{ borderRadius: 8, overflow: 'hidden', border: `1px solid ${T.border}`, cursor: 'pointer', transition: 'transform 0.15s, box-shadow 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.03)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.35)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <img src={photoUrl(p)} alt="before" style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block' }}/>
              </div>
            ))}
          </div>
        </>
      )}
      {after.length > 0 && (
        <>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: T.green, marginBottom: 10 }}>AFTER ({after.length})</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 6 }}>
            {after.map((p, i) => (
              <div
                key={i}
                onClick={() => setLightbox({ images: after, start: i })}
                style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(34,197,94,0.25)', cursor: 'pointer', transition: 'transform 0.15s, box-shadow 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.03)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.35)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <img src={photoUrl(p)} alt="after" style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block' }}/>
              </div>
            ))}
          </div>
        </>
      )}
      {lightbox && (
        <Lightbox images={lightbox.images} startIndex={lightbox.start} onClose={() => setLightbox(null)}/>
      )}
    </>
  );
};

export default CustomerPortal;